/**
 * Admin Sidebar
 *
 * Sidebar for the Bimanova admin portal.
 * - Grouped navigation with collapsible group sections
 * - Profile click opens a popover with Settings, Help, Sign out
 */

import { ChevronDown, ChevronRight, HelpCircle, LogOut, PanelLeft, PanelLeftClose, Settings } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';
import { useAuthStore } from '@/core/auth/auth.store';
import { useUIStore } from '@/state/stores/ui.store';
import { cn } from '@/shared/lib';
import { type PlatformRole } from '@/core/auth/constants';
import { ROUTE_PATHS } from '@/navigation/constants';
import { ADMIN_NAV_GROUPS, type AdminNavItem } from '../config/admin-navigation.config';

interface AdminSidebarProps {
  className?: string;
}

const AdminNavItemButton = ({
  item,
  isActive,
  isCollapsed,
}: {
  item: AdminNavItem;
  isActive: boolean;
  isCollapsed: boolean;
}) => {
  const navigate = useNavigate();
  const Icon = item.icon;

  return (
    <button
      onClick={() => navigate(item.path)}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        isActive
          ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100'
          : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100',
        isCollapsed && 'justify-center px-2'
      )}
      title={isCollapsed ? item.label : undefined}
    >
      <div className={cn('flex-shrink-0 flex items-center justify-center', isCollapsed ? 'w-6 h-6' : 'w-5 h-5')}>
        <Icon className="w-5 h-5" />
      </div>
      {!isCollapsed && (
        <span className="flex-1 text-left text-sm font-medium truncate">{item.label}</span>
      )}
      {!isCollapsed && item.badge !== undefined && item.badge > 0 && (
        <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-primary-500 text-white">
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
    </button>
  );
};

export const AdminSidebar = ({ className }: AdminSidebarProps) => {
  const { isSidebarCollapsed, toggleSidebarCollapse } = useUIStore();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  const userRole = user?.role as PlatformRole | undefined;

  // Close profile menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isVisible = (item: AdminNavItem): boolean => {
    if (item.visibleTo === 'all') return true;
    if (!userRole) return false;
    return item.visibleTo.includes(userRole);
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const isGroupCollapsed = (groupId: string) =>
    !isSidebarCollapsed && collapsedGroups.has(groupId);

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'A'
    : 'A';

  const handleLogout = async () => {
    setShowProfileMenu(false);
    await logout();
    navigate('/login');
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-screen bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800 z-40',
        'flex flex-col transition-all duration-300 ease-in-out',
        isSidebarCollapsed ? 'w-[72px]' : 'w-64',
        className
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'flex items-center h-16 px-4 border-b border-neutral-200 dark:border-neutral-800 flex-shrink-0',
          isSidebarCollapsed ? 'justify-center' : 'justify-between'
        )}
      >
        {!isSidebarCollapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">B</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-neutral-900 dark:text-neutral-100">Bimanova</span>
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-700 leading-none uppercase tracking-wide">
                Admin
              </span>
            </div>
          </div>
        )}
        {isSidebarCollapsed && (
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
            <span className="text-white font-bold text-sm">B</span>
          </div>
        )}
        {!isSidebarCollapsed && (
          <button
            onClick={toggleSidebarCollapse}
            className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Main Nav — grouped */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto">
        {ADMIN_NAV_GROUPS.map((group, groupIndex) => {
          const visibleItems = group.items.filter(isVisible);
          if (visibleItems.length === 0) return null;
          const collapsed = isGroupCollapsed(group.id);

          return (
            <div key={group.id} className={groupIndex > 0 ? 'mt-4' : ''}>
              {group.label && !isSidebarCollapsed && (
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full flex items-center justify-between px-3 pb-1.5 group/header"
                >
                  <span className="text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                    {group.label}
                  </span>
                  <ChevronDown
                    className={cn(
                      'w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500 transition-transform duration-200',
                      collapsed && '-rotate-90'
                    )}
                  />
                </button>
              )}
              {!collapsed && (
                <div className="space-y-0.5">
                  {visibleItems.map((item) => (
                    <AdminNavItemButton
                      key={item.id}
                      item={item}
                      isActive={isActive(item.path)}
                      isCollapsed={isSidebarCollapsed}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Profile area with popover */}
      <div ref={profileRef} className="relative border-t border-neutral-200 dark:border-neutral-800 flex-shrink-0">
        {/* Profile menu popover */}
        {showProfileMenu && (
          <div className="absolute bottom-full left-3 right-3 mb-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-lg overflow-hidden z-50">
            {!isSidebarCollapsed && (
              <>
                <div className="px-4 py-3 border-b border-neutral-100 dark:border-neutral-700">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate capitalize mt-0.5">
                    {user?.role?.replace(/_/g, ' ').toLowerCase()}
                  </p>
                </div>
              </>
            )}
            <div className="p-1">
              <button
                onClick={() => { navigate(ROUTE_PATHS.ADMIN_SETTINGS); setShowProfileMenu(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
              >
                <Settings className="w-4 h-4 flex-shrink-0" />
                <span>Settings</span>
              </button>
              <button
                onClick={() => { navigate(ROUTE_PATHS.ADMIN_HELP); setShowProfileMenu(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
              >
                <HelpCircle className="w-4 h-4 flex-shrink-0" />
                <span>Help</span>
              </button>
            </div>
            <div className="h-px bg-neutral-100 dark:bg-neutral-700 mx-1" />
            <div className="p-1">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <LogOut className="w-4 h-4 flex-shrink-0" />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        )}

        {/* Profile button */}
        <div className="px-3 py-3">
          <button
            onClick={() => setShowProfileMenu(prev => !prev)}
            className={cn(
              'w-full flex items-center gap-3 p-2 rounded-lg transition-colors',
              showProfileMenu
                ? 'bg-neutral-100 dark:bg-neutral-800'
                : 'hover:bg-neutral-100 dark:hover:bg-neutral-800',
              isSidebarCollapsed && 'justify-center'
            )}
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-medium">
              {initials}
            </div>
            {!isSidebarCollapsed && (
              <>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate capitalize">
                    {user?.role?.replace(/_/g, ' ').toLowerCase()}
                  </p>
                </div>
                <ChevronRight
                  className={cn(
                    'w-4 h-4 text-neutral-400 flex-shrink-0 transition-transform duration-200',
                    showProfileMenu && 'rotate-90'
                  )}
                />
              </>
            )}
          </button>

          {isSidebarCollapsed && (
            <button
              onClick={toggleSidebarCollapse}
              className="w-full flex items-center justify-center p-2 mt-2 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
              title="Expand sidebar"
            >
              <PanelLeft className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;
